// Self-hosted Academy content catalog.

export const ACADEMY_HERO = {
  eyebrow: 'ZHELTA ACADEMY',
  titleA: 'Empower Your Career.',
  titleB: 'Master In-Demand Skills.',
  titleC: 'Learn with ZHELTA.',
  body: "Accessible, expert-led education designed to equip learners with practical skills for today's digital world.",
  primaryCTA: { label: 'Explore All Programs', href: '#programs' },
  note: 'ZHELTA Academy provides practical, future-ready education aligned with real-world needs.',
};

export const ACADEMY_WHY = {
  eyebrow: 'Why Choose Us',
  title: 'Future-Ready Learning',
  quote:
    "Transforming today's potential into tomorrow's innovation through a hyper-flexible, mentor-driven IT curriculum.",
  items: [
    {
      tag: 'In Demand',
      icon: 'Target',
      title: 'Industry-Aligned Skills',
      body:
        'Master practical certifications and technical skills shaped directly by real-world technology needs and feedback from our network of top-tier employers.',
      chips: ['Cloud', 'Cyber', 'Data', 'Web', 'Logic', 'AI'],
    },
    {
      tag: 'Personalized',
      icon: 'CalendarClock',
      title: 'Flexible Learning',
      body: 'Total autonomy over your schedule. Design a path that fits your life.',
      chips: ['M', 'T', 'W', 'T', 'F'],
      note: 'Live Session · 14:00 - 16:00',
    },
    {
      tag: '24/7 Mentors',
      icon: 'LifeBuoy',
      title: 'Guided Support',
      body: '24/7 access to expert mentors invested in your specific career trajectory.',
    },
    {
      tag: 'Outcomes',
      icon: 'Trophy',
      title: 'Career Paths',
      body: 'Hyper-structured outcomes that move you from curiosity to certification to employment.',
    },
  ],
};

export const ACADEMY_PROGRAMS = {
  eyebrow: 'World-Class Curriculum',
  title: 'Explore All Our Programs',
  subtitle:
    'Explore our comprehensive range of programs designed to equip you with the skills and knowledge for a successful career in IT and digital fields.',
  items: [
    {
      icon: 'Award',
      title: 'IT Certifications',
      body: 'Prepare for globally recognized IT certifications like CompTIA A+, Network+, Security+, AWS, and Azure to qualify for entry-level IT roles.',
      gradient: 'from-amber-400 to-orange-500',
      href: '/academy/programs/it-certification-programs',
      learn: ['CompTIA A+ / Network+ / Security+', 'AWS & Azure cloud foundations', 'Help-desk troubleshooting playbooks'],
    },
    {
      icon: 'Cpu',
      title: 'Computer Hardware',
      body: 'Master computer hardware components, system architecture, and troubleshooting to become a skilled PC Technician or IT Support Assistant.',
      gradient: 'from-slate-600 to-slate-800',
      href: '/academy/programs/computer-hardware-system-fundamentals',
      learn: ['Component-level diagnostics', 'PC build & teardown labs', 'Storage, RAM and BIOS deep dives'],
    },
    {
      icon: 'MonitorCog',
      title: 'Software Support',
      body: 'Learn to install, configure, and support software across multiple operating systems for Help Desk and Support Technician roles.',
      gradient: 'from-sky-500 to-indigo-600',
      href: '/academy/programs/software-installation-use-support',
      learn: ['Windows / macOS / Linux support', 'Ticketing & SLA workflows', 'Remote-desktop troubleshooting'],
    },
    {
      icon: 'ShieldCheck',
      title: 'Cybersecurity',
      body: 'Develop essential cybersecurity skills to identify threats, prevent attacks, and prepare for careers in cybersecurity support and compliance.',
      gradient: 'from-emerald-500 to-teal-600',
      href: '/academy/programs/cybersecurity-awareness-best-practices',
      learn: ['Phishing & social-engineering defense', 'Endpoint hardening basics', 'Compliance & best-practice frameworks'],
    },
    {
      icon: 'Code2',
      title: 'Programming',
      body: 'Build software applications using Python, HTML, JavaScript, and databases while learning version control to start your developer career.',
      gradient: 'from-violet-500 to-fuchsia-600',
      href: '/academy/programs/programming-software-development',
      learn: ['Python fundamentals + projects', 'HTML / CSS / JavaScript', 'Git & GitHub workflows'],
    },
    {
      icon: 'Brain',
      title: 'IT Logic',
      body: 'Develop mathematical reasoning and logical problem-solving skills essential for data science, programming, and tech analytics careers.',
      gradient: 'from-rose-500 to-pink-600',
      href: '/academy/programs/mathematics-logical-thinking-it',
      learn: ['Logic, sets and algorithms', 'Data-friendly statistics primer', 'Problem-decomposition drills'],
    },
    {
      icon: 'Vote',
      title: 'Citizenship',
      body: 'Learn to use technology responsibly and ethically while protecting against misinformation and preparing for digital advocacy roles.',
      gradient: 'from-cyan-500 to-blue-600',
      href: '/academy/programs/digital-citizenship-civic-education',
      learn: ['Misinformation literacy', 'Privacy and digital rights', 'Civic-tech case studies'],
    },
    {
      icon: 'MessageCircle',
      title: 'Communication',
      body: 'Master professional communication and technical documentation skills for IT administrative and project coordination roles.',
      gradient: 'from-orange-400 to-red-500',
      href: '/academy/programs/professional-communication-it-documentation',
      learn: ['Technical writing patterns', 'Email + ticket etiquette', 'Stakeholder reporting templates'],
    },
    {
      icon: 'Palette',
      title: 'Media Literacy',
      body: 'Design visual content using modern tools like Canva and Figma while learning UX/UI principles for creative technology careers.',
      gradient: 'from-pink-500 to-rose-600',
      href: '/academy/programs/creative-technology-media-literacy',
      learn: ['Canva + Figma essentials', 'UX/UI heuristics', 'Mini brand-system project'],
    },
    {
      icon: 'Briefcase',
      title: 'Role-Based Learning Pathways',
      body: 'Complete specialized career pathways with portfolio development to become job-ready for specific IT roles like Data Analyst or Web Developer.',
      gradient: 'from-stone-600 to-stone-800',
      href: '/academy/programs/role-based-learning-portals',
      learn: ['Choose: Data Analyst / Web Dev / Support', 'Portfolio-grade capstone', 'Mock interviews + feedback'],
    },
    {
      icon: 'Sparkles',
      title: 'Learning Boost Program',
      body: 'GES-Aligned Academic Support & Remedial Learning designed specifically to help Ghanaian learners excel.',
      gradient: 'from-yellow-400 to-amber-600',
      href: '/academy/programs/learning-boost-program',
      learn: ['GES-aligned remedial tracks', 'Maths + ICT + English boosters', 'Tutor check-ins every 2 weeks'],
    },
  ],
  footer: {
    label: 'View All Programs',
    href: '/academy/programs',
  },
};

export const ACADEMY_TRACKS = {
  eyebrow: 'Growth Path',
  title: 'Start at Your Level, Grow Your Expertise',
  subtitle:
    'Our learning tracks are tailored for beginner, intermediate, and advanced learners, ensuring a perfect starting point.',
  items: [
    {
      level: 'Level 1',
      title: 'Beginner Tracks',
      body: 'New to the field? Start with foundational concepts and build a solid understanding needed for your career journey.',
      cta: 'Start Learning',
      gradient: 'from-[#c9a227] to-[#e6c35a]',
      icon: 'Sparkles',
      skills: ['Computer literacy', 'Hardware basics', 'Help desk fundamentals'],
      programsCount: 4,
    },
    {
      level: 'Level 2',
      title: 'Intermediate Tracks',
      body: 'Build upon existing knowledge and develop more specialized, in-depth skills that set you apart in the industry.',
      cta: 'Deep Dive',
      gradient: 'from-slate-700 to-slate-900',
      icon: 'TrendingUp',
      skills: ['Programming', 'Cybersecurity awareness', 'Networking essentials'],
      programsCount: 4,
    },
    {
      level: 'Level 3',
      title: 'Advanced Tracks',
      body: 'Master advanced topics, refine your expertise, and prepare for expert-level roles and leadership positions.',
      cta: 'Master It',
      gradient: 'from-[#1a2332] to-[#0b0f19]',
      icon: 'Crown',
      skills: ['Cloud (AWS / Azure)', 'Role-based capstones', 'Career portfolio'],
      programsCount: 2,
    },
  ],
};

export const ACADEMY_JOURNEY = {
  eyebrow: 'Your Journey',
  title: 'Four Simple Steps to Success',
  steps: [
    {
      n: '1',
      icon: 'Search',
      title: 'Explore & Enroll',
      body: 'Browse our diverse catalog and easily enroll in your chosen course or path.',
    },
    {
      n: '2',
      icon: 'Clock',
      title: 'Learn at Your Pace',
      body: 'Access content online, anytime, anywhere, and progress self-paced.',
    },
    {
      n: '3',
      icon: 'MessagesSquare',
      title: 'Engage & Get Support',
      body: 'Connect with instructors, mentors, & fellow learners. Get guidance you need.',
    },
    {
      n: '4',
      icon: 'Rocket',
      title: 'Achieve & Advance',
      body: 'Earn certifications, build skills, & take the next step in your career.',
    },
  ],
};

export const ACADEMY_OUTCOMES = {
  eyebrow: 'Outcomes',
  title: 'After ZHELTA, you walk away with',
  subtitle: 'Tangible artefacts you can show on a CV, LinkedIn, or in an interview.',
  items: [
    {
      icon: 'Award',
      title: 'A verifiable certificate',
      body: 'Branded PDF + a public verification page so employers can confirm authenticity in one click.',
      stat: 'Public verify URL',
    },
    {
      icon: 'Briefcase',
      title: 'A portfolio of real projects',
      body: 'Build, ship and document at least one project per program — not just slides and quizzes.',
      stat: '1+ project / program',
    },
    {
      icon: 'Trophy',
      title: 'Career-ready signal',
      body: 'Mock-interview feedback, role-fit guidance, and a mentor reference for top performers.',
      stat: 'Mentor reference',
    },
    {
      icon: 'Rocket',
      title: 'Roles you can apply for',
      body: 'IT Support · PC Technician · Junior Developer · Cybersecurity Analyst · Data Analyst · Help-Desk Lead.',
      stat: '6+ entry-level roles',
    },
  ],
};

export const ACADEMY_CTA = {
  eyebrow: 'Start Your Journey',
  title: 'Ready to Transform Your Future?',
  body:
    'Join ZHELTA Academy and master in-demand skills with our expert-led programs and mentorship.',
  primaryCTA: { label: 'Browse Programs', href: '/academy/programs' },
  secondaryCTA: { label: 'View Tracks', href: '#tracks' },
};
